import { ApplicationRef, EnvironmentInjector, Injectable, Injector, createComponent } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { SystemToastsComponent } from '../system-notifications/system-toasts.component';

export type NoticeSeverity = 'error' | 'warn' | 'info' | 'success';

export interface SystemNotice {
  /** stable id for a kind of problem (e.g. 'pos', 'tables', 'ws') — one notice per key */
  key: string;
  severity: NoticeSeverity;
  message: string;
  /** when the notice first appeared (kept across identical re-sets, shown in the bar) */
  time: Date;
  /** when true the bar shows a close (×) button; clicking it calls dismiss(key). */
  dismissible?: boolean;
}

/**
 * Holds the set of currently-active system notices, one per `key`, in two layers:
 *
 * - Persistent (no `autoDismissMs`): rendered by the in-flow info bar
 *   (app-system-notifications) right below the header — long-lived status may occupy space.
 * - Transient (`autoDismissMs` set): rendered by a floating toast stack that overlays the
 *   content instead of pushing it down for a few seconds. The stack (SystemToastsComponent)
 *   is created here, once, directly on <body> — apps declare nothing.
 *
 * Sources call `set(key, …)` when a problem appears and `clear(key)` the moment it is
 * resolved (e.g. on the matching successful request) — instead of re-toasting every poll.
 *
 * Dismissible notices (e.g. operator service messages) additionally render a × button; the
 * component calls `dismiss(key)`, which removes the notice and emits the key on `dismissed$`
 * so the source can remember the dismissal (e.g. persist to localStorage).
 */
@Injectable({ providedIn: 'root' })
export class SystemStatusService {
  private readonly _notices = new BehaviorSubject<SystemNotice[]>([]);
  readonly notices$ = this._notices.asObservable();

  /** Transient (autoDismissMs) notices — consumed by the floating toast layer, not the bar. */
  private readonly _toasts = new BehaviorSubject<SystemNotice[]>([]);
  readonly toasts$ = this._toasts.asObservable();

  /** Emits the key of a notice the user closed via its × button. */
  readonly dismissed$ = new Subject<string>();

  /** Pending auto-dismiss timers, one per key. */
  private readonly timers = new Map<string, any>();

  /** true once the floating toast layer has been attached to <body>. */
  private toastsHostMounted = false;

  constructor(private readonly injector: Injector) {}

  /**
   * Add/replace the notice for `key`. No-op (no flicker) if an identical one is already shown.
   * `autoDismissMs` (optional) auto-clears the notice after that delay — such transient notices
   * (success confirmations, one-off errors — the old toasts) go to the floating toast layer.
   * Persistent status (POS, subscription) omits it, renders in the in-flow bar, and is cleared
   * by the source on resolution.
   */
  set(key: string, severity: NoticeSeverity, message: string, dismissible: boolean = false, autoDismissMs?: number): void {
    if (autoDismissMs && autoDismissMs > 0) {
      this.setToast(key, severity, message, autoDismissMs);
      return;
    }
    this.clearTimer(key);
    this.remove(this._toasts, key); // a key lives in exactly one layer
    const cur = this._notices.value;
    const existing = cur.find(n => n.key === key);
    const identical = existing && existing.severity === severity && existing.message === message
      && !!existing.dismissible === dismissible;
    if (!identical) {
      // keep the original first-appeared time when only the message changes (e.g. the
      // ws retry counter ticking up) so the bar still shows when the problem started.
      const time = existing ? existing.time : new Date();
      this._notices.next(cur.filter(n => n.key !== key).concat({ key, severity, message, time, dismissible }));
    }
  }

  private setToast(key: string, severity: NoticeSeverity, message: string, autoDismissMs: number): void {
    this.remove(this._notices, key);
    const cur = this._toasts.value;
    const existing = cur.find(n => n.key === key);
    const identical = existing && existing.severity === severity && existing.message === message;
    if (!identical) {
      const time = existing ? existing.time : new Date();
      this._toasts.next(cur.filter(n => n.key !== key).concat({ key, severity, message, time }));
    }
    // (re)arm also on an identical re-set, so the toast stays the full delay again
    this.armAutoDismiss(key, autoDismissMs);
    this.ensureToastsHost();
  }

  /** Remove the notice for `key` from either layer (auto-dismiss when the problem is resolved). */
  clear(key: string): void {
    this.clearTimer(key);
    this.remove(this._notices, key);
    this.remove(this._toasts, key);
  }

  private remove(layer: BehaviorSubject<SystemNotice[]>, key: string): void {
    const cur = layer.value;
    if (cur.some(n => n.key === key)) {
      layer.next(cur.filter(n => n.key !== key));
    }
  }

  /**
   * Mounts the floating toast layer once, directly on <body>, on the first toast — no app
   * template declares it. ApplicationRef is resolved lazily here: the service itself is often
   * constructed during bootstrap (http interceptor → translate loader), where injecting
   * ApplicationRef in the constructor risks a circular-DI error.
   */
  private ensureToastsHost(): void {
    if (this.toastsHostMounted) {
      return;
    }
    this.toastsHostMounted = true;
    const ref = createComponent(SystemToastsComponent, { environmentInjector: this.injector.get(EnvironmentInjector) });
    ref.instance.toasts$ = this.toasts$;
    ref.instance.dismiss = key => this.clear(key);
    document.body.appendChild(ref.location.nativeElement);
    this.injector.get(ApplicationRef).attachView(ref.hostView);
  }

  private armAutoDismiss(key: string, autoDismissMs?: number): void {
    this.clearTimer(key);
    if (autoDismissMs && autoDismissMs > 0) {
      this.timers.set(key, setTimeout(() => { this.timers.delete(key); this.clear(key); }, autoDismissMs));
    }
  }

  private clearTimer(key: string): void {
    const t = this.timers.get(key);
    if (t) { clearTimeout(t); this.timers.delete(key); }
  }

  /** User closed the notice via its × button — remove it and notify the source. */
  dismiss(key: string): void {
    this.clear(key);
    this.dismissed$.next(key);
  }
}
